import React, { useEffect } from 'react';
import { FieldErrors, UseFormWatch } from 'react-hook-form';
import { ActionErrorEntry } from '../decom-policy-utils.tsx';
import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';
import BoxedIcon from '../../../../../shared/boxed-icon.tsx';
import SharedIconButton from '../../../../../shared/buttons/shared-icon-button.tsx';
import { getActionIcon } from './actions-accordion-icons.tsx';
import ExpandCollapseButton from '../../../../../shared/buttons/expand-collapse-button.tsx';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

interface ActionAccordionProps {
  id: string;
  groupType: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  expanded: boolean;
  onToggleExpand: (isExpanded: boolean) => void;
  onRemove?: () => void;
  children?: React.ReactNode;
  errors: FieldErrors<DecomPolicyFormValues>;
  watch: UseFormWatch<DecomPolicyFormValues>;
  actionTypesWithErrors: ActionErrorEntry[];
  day: number;
}

export default function ActionAccordion({
  id,
  groupType,
  title,
  description,
  expanded,
  onToggleExpand,
  onRemove,
  children,
  errors,
  actionTypesWithErrors,
  watch,
  day,
}: ActionAccordionProps) {
  const theme = useTheme();
  const actionIcon = getActionIcon(groupType);
  const actionsData = watch(`actions.${day}.actions`) || [];
  const matchingIndex = actionsData.findIndex((a) => a.actionType === id);

  const matchingIndexInitErrors = actionTypesWithErrors.findIndex(
    (a) => a.id === id
  );

  const actionErrors = errors?.actions?.[day]?.actions?.[matchingIndex]
    ? errors.actions?.[day]?.actions?.[matchingIndex]
    : [];
  const actionInitErrors =
    actionTypesWithErrors && matchingIndexInitErrors !== -1
      ? actionTypesWithErrors[matchingIndexInitErrors]?.errors
      : null;

  const hasActionErrors =
    !!actionErrors && Object.keys(actionErrors).length > 0;
  const hasInitErrors =
    !!actionInitErrors && Object.keys(actionInitErrors).length > 0;

  const hasErrors =
    Boolean(actionErrors && Object.keys(actionErrors).length) &&
    Boolean(actionInitErrors && Object.keys(actionInitErrors).length);
  const showWarningIcon = hasActionErrors || hasInitErrors;

  const getStatusIndicator = () => {
    if (!groupType) return null;

    return (
      <Typography
        variant="caption"
        sx={{
          display: 'flex',
          alignItems: 'center',
          lineHeight: 1,
          gap: 0.5,
          mr: 1,
          whiteSpace: 'nowrap',
          color: showWarningIcon
            ? theme.palette.warning.main
            : theme.palette.text.secondary,
        }}
      >
        {showWarningIcon ? (
          <>
            <WarningAmberOutlined sx={{ fontSize: '12px' }} />
            Needs Attention
          </>
        ) : (
          <>
            <CheckCircleOutline sx={{ fontSize: '12px' }} />
            No configuration needed
          </>
        )}
      </Typography>
    );
  };

  const handleToggleExpand = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    onToggleExpand(!expanded);
  };

  const handleAccordionRemove = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    if (!onRemove) return;
    e.stopPropagation();
    onRemove();
  };

  useEffect(() => {
    if (hasErrors) {
      onToggleExpand(true);

      let firstId: string | undefined;

      if (actionTypesWithErrors.length > 0) {
        firstId = actionTypesWithErrors[actionTypesWithErrors.length - 1].id;
      } else if (actionErrors && typeof actionErrors === 'object') {
        const firstKey = Object.keys(
          actionErrors
        )[0] as keyof typeof actionErrors;
        const errorVal = actionErrors[firstKey] as
          | Record<string, unknown>
          | undefined;
        const refName = (errorVal?.ref as { name?: string } | undefined)?.name;

        if (refName) {
          const match = refName.match(/actions\.(\d+)/);
          if (match) {
            const idx = parseInt(match[1]);
            const actionType = watch(
              `actions.${day}.actions.${idx}.actionType`
            );
            if (actionType) firstId = actionType;
          }
        }
      }

      if (firstId) {
        setTimeout(() => {
          const element = document.getElementById(firstId);
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionErrors]);

  return (
    <Accordion
      id={id}
      expanded={expanded}
      onChange={(_, isExpanded) => onToggleExpand(isExpanded)}
      disableGutters
      sx={{
        mb: 2,
        width: '100%',
        borderRadius: 0,
        overflow: 'hidden',
        boxShadow: `0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 3px 0px rgba(0,0,0,0.12)`,
        border: theme.palette.mode === 'dark' ? '1px solid' : 'none',
        borderColor:
          theme.palette.mode === 'dark'
            ? theme.palette.semantic.outline.light
            : 'none',
        position: 'relative',
        '&:before': { display: 'none' },
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          userSelect: 'none',
          minHeight: 64,
          gap: 1,
          cursor: 'pointer',
        }}
        onClick={() => onToggleExpand(!expanded)}
      >
        {actionIcon && (
          <BoxedIcon
            variant={actionIcon.variant}
            icon={actionIcon.icon}
            size={28}
          />
        )}

        <Box flex="1">
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: theme.palette.semantic.text.support,
              cursor: 'pointer',
            }}
          >
            {groupType}
          </Typography>
          {title && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.semantic.text.title2,
                mt: 0.25,
                fontWeight: 600,
              }}
            >
              {title}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {groupType && getStatusIndicator()}
          <ExpandCollapseButton
            onClick={handleToggleExpand}
            expanded={expanded}
          />

          {onRemove && (
            <SharedIconButton
              variant="delete"
              onClick={handleAccordionRemove}
              aria-label="Remove"
            />
          )}
        </Box>
      </Box>

      <AccordionSummary
        expandIcon={null}
        sx={{
          display: 'none',
        }}
      />

      <AccordionDetails
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: theme.palette.semantic.outline.light,
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: theme.palette.semantic.text.body, mb: 2 }}
        >
          {description}
        </Typography>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}
